import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { type CorrectionOut } from "@platform/api/golden";
import { Card, Typography } from "@platform/design-system";

import { formatValue } from "../shared/values";

import { useCaseCorrections } from "./caseQueries";
import { SectionStatus } from "./SectionStatus";

import type { ReactNode } from "react";

export const CASE_CORRECTIONS_TEST_ID = "golden-case-corrections";

const CORRECTED_TO: Readonly<
  Record<CorrectionOut["kind"], (correction: CorrectionOut) => string>
> = {
  value: (correction) => formatValue(correction.corrected_value),
  equivalent: (correction) =>
    `also accepts ${formatValue(correction.accepted_value)}`,
  exclude: () => "not scored",
};

/** Every correction on the case's baseline, with the ones a later verdict replaced struck through. */
export const CaseCorrections = ({
  caseId,
}: {
  readonly caseId: number;
}): ReactNode => {
  const corrections = useCaseCorrections(caseId);

  if (!corrections.isSuccess) {
    return (
      <Card data-testid={CASE_CORRECTIONS_TEST_ID} title="Corrections">
        <SectionStatus
          testId={CASE_CORRECTIONS_TEST_ID}
          label="corrections"
          error={corrections.error}
        />
      </Card>
    );
  }

  return (
    <Card data-testid={CASE_CORRECTIONS_TEST_ID} title="Corrections">
      <Table size="small" aria-label="Corrections">
        <TableHead>
          <TableRow>
            <TableCell>Rev.</TableCell>
            <TableCell>Field</TableCell>
            <TableCell>Kind</TableCell>
            <TableCell>Analyst sent</TableCell>
            <TableCell>Corrected to</TableCell>
            <TableCell>By</TableCell>
            <TableCell>Reason</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {corrections.data.map((correction) => {
            const isReplaced =
              correction.superseded_by != null ||
              correction.revoked_revision != null;
            return (
              <TableRow
                key={correction.id}
                data-testid={`${CASE_CORRECTIONS_TEST_ID}-row-${correction.id}`}
                aria-label={isReplaced ? "Replaced correction" : undefined}
                sx={
                  isReplaced
                    ? {
                        "& td": {
                          textDecoration: "line-through",
                          color: "text.secondary",
                        },
                      }
                    : undefined
                }
              >
                <TableCell>
                  {correction.correction_revision}
                  {correction.revoked_revision != null && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      component="span"
                    >
                      {" "}
                      (revoked at {correction.revoked_revision})
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>
                  {correction.field_path}
                </TableCell>
                <TableCell>{correction.kind}</TableCell>
                <TableCell>{formatValue(correction.baseline_value)}</TableCell>
                <TableCell>
                  {CORRECTED_TO[correction.kind](correction)}
                </TableCell>
                <TableCell>{correction.reviewer}</TableCell>
                <TableCell>{correction.reason ?? ""}</TableCell>
              </TableRow>
            );
          })}
          {corrections.data.length === 0 && (
            <TableRow data-testid={`${CASE_CORRECTIONS_TEST_ID}-empty`}>
              <TableCell colSpan={7}>No corrections.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Card>
  );
};
